import type {RichTextField} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import {useRouter} from 'next/router'
import {useCallback} from 'react'
import type {UrlObject} from 'url'
// eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
import {LeadIdProvider} from 'slices/ProductTourSection/context/LeadIdContext'
import {DeferredVideo} from 'src/common/DeferredVideo'
import {LeadGenerationField} from 'src/common/LeadGenerationField'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'
import {AnimatedBlobBackground} from './AnimatedBlobBackground'

export type DefaultLeadCaptureBlobSectionUIProps = {
  eyebrow: string
  title: RichTextField
  description: string
  videoUrl: string | null
  videoPosterUrl?: string | null
  placeholder: string
  submitButtonLabel: string
  submitLink: string | UrlObject | null
  genericError: string
  disclaimer: string
  pageUid: string
  paddingTop?: string | null
  paddingBottom?: string | null
}

export const DefaultLeadCaptureBlobSection = ({
  eyebrow,
  title,
  description,
  videoUrl,
  videoPosterUrl,
  placeholder,
  submitButtonLabel,
  submitLink,
  genericError,
  disclaimer,
  pageUid,
  paddingTop,
  paddingBottom,
}: DefaultLeadCaptureBlobSectionUIProps) => {
  const router = useRouter()
  const paddingTopClass = PADDING_TOP_CLASS[paddingTop ?? DEFAULT_PADDING] ?? ''
  const paddingBottomClass = PADDING_BOTTOM_CLASS[paddingBottom ?? DEFAULT_PADDING] ?? ''

  const handleSubmitSuccess = useCallback(() => {
    if (submitLink) {
      router.push(submitLink)
    }
  }, [router, submitLink])

  return (
    <LeadIdProvider>
      <section
        data-slice-type="blob_section"
        data-slice-variation="defaultLeadCapture"
        data-nav-dark="true"
        className={cn(
          'relative flex min-h-[500px] items-center overflow-hidden bg-green-dark px-5 md:min-h-[650px] md:px-10 lg:min-h-[825px] lg:px-[120px]',
          paddingTopClass,
          paddingBottomClass,
          'max-md:!pb-24',
          '[&:first-child]:pt-40',
        )}
      >
        <AnimatedBlobBackground />
        <div className="relative z-10 flex w-full flex-col items-center gap-12 lg:gap-[120px]">
          <div className="flex flex-col items-center gap-8">
            <div className="flex flex-col items-center gap-4 text-center">
              {eyebrow && (
                <p className="font-main text-12 font-semibold uppercase tracking-[0.6px] text-moss-white">{eyebrow}</p>
              )}
              <h1 className="flex flex-col">
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({children}) => (
                      <span className="font-main text-40 leading-[42px] tracking-[-0.4px] text-moss-white md:leading-tight lg:text-64 lg:leading-[64px] lg:tracking-[-0.64px]">
                        {children}
                      </span>
                    ),
                    em: () => <br />,
                  }}
                />
              </h1>
              {description && (
                <p className="max-w-[432px] font-main text-18 leading-6 text-moss-white">{description}</p>
              )}
            </div>
            <div className="flex flex-col items-center gap-3">
              <LeadGenerationField
                pageUid={pageUid}
                placeholder={placeholder}
                submitButtonText={submitButtonLabel}
                onSuccess={handleSubmitSuccess}
                errorMessage={genericError || undefined}
                tracking={{
                  recaptchaAction: 'blob_section_input',
                  formName: 'blob-section-input-form',
                  intent: 'blob-section-input',
                  location: 'blob-section-input',
                }}
              />
              {disclaimer && (
                <p className="text-center font-main text-12 font-semibold uppercase tracking-[0.6px] text-moss-white/70">
                  {disclaimer}
                </p>
              )}
            </div>
          </div>
          {videoUrl && (
            <div className="aspect-video w-full max-w-[1200px] overflow-hidden rounded-[6.4px]">
              <DeferredVideo videoUrl={videoUrl} posterUrl={videoPosterUrl} className="size-full object-cover" />
            </div>
          )}
        </div>
      </section>
    </LeadIdProvider>
  )
}
